import { jsxs, jsx, Fragment as Fragment$1 } from "react/jsx-runtime";
import { useTranslations } from "@vocab/react";
import { Stack, Columns, Column, Text, IconLocation, Link, IconCompany, IconTime, Inline, Box, IconMoney, ButtonIcon, IconInfo, Dialog } from "braid-design-system";
import { Fragment, useState } from "react";
import { useJobDetailsViewContext } from "../ApplySection/ApplyButton/ApplyButton.mjs";
import { useAnalyticsHandlers } from "../CompanyProfile/CompanyProfile.mjs";
import { translations } from "../../../components/JobDetailsViewHeaderAdditional/.vocab/index.mjs";
import { metaLink } from "./JobDetailsViewHeaderAdditional.css.mjs";
const JobLocation = ({
  location,
  locationUrl,
  workArrangementLabel
}) => /* @__PURE__ */ jsxs(Columns, { space: "small", children: [
  /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(Text, { size: "small", children: /* @__PURE__ */ jsx(IconLocation, { tone: "secondary" }) }) }),
  /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(Stack, { space: "small", children: /* @__PURE__ */ jsxs(Text, { size: "small", data: { automation: "job-detail-location" }, children: [
    locationUrl ? /* @__PURE__ */ jsx(
      Link,
      {
        className: metaLink,
        tabIndex: -1,
        href: locationUrl || "#",
        children: location
      }
    ) : location,
    workArrangementLabel ? ` (${workArrangementLabel})` : ""
  ] }) }) })
] });
const JobClassifications = ({
  classifications,
  classificationUrls
}) => /* @__PURE__ */ jsxs(Columns, { space: "small", children: [
  /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(Text, { size: "small", children: /* @__PURE__ */ jsx(IconCompany, { tone: "secondary" }) }) }),
  /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(Stack, { space: "small", children: /* @__PURE__ */ jsx(Text, { size: "small", data: { automation: "job-detail-classifications" }, children: classifications.map((classification, index) => /* @__PURE__ */ jsxs(Fragment, { children: [
    classificationUrls && classificationUrls[index] ? /* @__PURE__ */ jsx(
      Link,
      {
        className: metaLink,
        tabIndex: -1,
        href: classificationUrls[index],
        children: classification
      }
    ) : /* @__PURE__ */ jsx(Fragment$1, { children: classification }),
    index < classifications.length - 1 && /* @__PURE__ */ jsx("br", {})
  ] }, `${classification}_${index}`)) }) }) })
] });
const JobWorkType = ({
  workType,
  workTypeUrl
}) => /* @__PURE__ */ jsxs(Columns, { space: "small", children: [
  /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(Text, { size: "small", children: /* @__PURE__ */ jsx(IconTime, { tone: "secondary" }) }) }),
  /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(Stack, { space: "small", children: /* @__PURE__ */ jsx(Text, { size: "small", data: { automation: "job-detail-work-type" }, children: workTypeUrl ? /* @__PURE__ */ jsx(
    Link,
    {
      className: metaLink,
      tabIndex: -1,
      href: workTypeUrl || "#",
      children: workType
    }
  ) : workType }) }) })
] });
const JobSalary = ({
  salary,
  currencyLabel,
  isSalaryMatch,
  isSignedIn,
  salaryExpectation,
  isCandidateSetSalary,
  salaryMatchCountryName
}) => isSignedIn ? /* @__PURE__ */ jsx(
  JobSalarySignedIn,
  {
    salary,
    currencyLabel,
    isSalaryMatch,
    salaryExpectation,
    isCandidateSetSalary,
    salaryMatchCountryName
  }
) : /* @__PURE__ */ jsx(JobSalarySignedOut, { salary, currencyLabel });
const JobSalarySignedIn = ({
  salary,
  currencyLabel,
  isSalaryMatch,
  salaryExpectation,
  isCandidateSetSalary,
  salaryMatchCountryName
}) => {
  if (isSalaryMatch) {
    return /* @__PURE__ */ jsx(Stack, { space: "xsmall", children: /* @__PURE__ */ jsx(
      SalaryTransparencyBadge,
      {
        salary,
        salaryExpectation,
        salaryMatchCountryName,
        currencyLabel
      }
    ) });
  }
  if (!salary) {
    if (!isCandidateSetSalary) {
      return /* @__PURE__ */ jsx(AddExpectedSalary, {});
    }
  }
  return salary && /* @__PURE__ */ jsx(FormattedSalary, { salary, currencyLabel });
};
const JobSalarySignedOut = ({ salary, currencyLabel }) => {
  if (!salary) {
    return /* @__PURE__ */ jsx(AddExpectedSalary, {});
  }
  return /* @__PURE__ */ jsx(FormattedSalary, { salary, currencyLabel });
};
const SalaryTransparencyBadge = ({
  salary,
  salaryExpectation,
  salaryMatchCountryName,
  currencyLabel
}) => {
  const { onAnalyticsEvent } = useJobDetailsViewContext();
  const [iconInfoPopup, setIconInfoPopup] = useState(false);
  const { t } = useTranslations(translations);
  const analytics = useAnalyticsHandlers(onAnalyticsEvent);
  const formattedSalary = currencyLabel ? `${salary} (${currencyLabel})` : salary;
  return /* @__PURE__ */ jsxs(Box, { children: [
    salary ? /* @__PURE__ */ jsxs(Columns, { space: "small", children: [
      /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(Text, { size: "small", children: /* @__PURE__ */ jsx(IconMoney, { tone: "secondary" }) }) }),
      /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsxs(Stack, { space: "xsmall", children: [
        /* @__PURE__ */ jsx(Text, { size: "small", data: { automation: "job-detail-salary" }, children: formattedSalary }),
        /* @__PURE__ */ jsxs(Inline, { space: "xxsmall", alignY: "center", children: [
          /* @__PURE__ */ jsx(
            Text,
            {
              weight: "strong",
              tone: "positive",
              size: "xsmall",
              data: { automation: "job-detail-salary-match" },
              children: t("Profile salary match")
            }
          ),
          /* @__PURE__ */ jsx(
            ButtonIcon,
            {
              size: "small",
              variant: "transparent",
              label: t("Salary match details"),
              id: "salaryMatchDetails",
              icon: /* @__PURE__ */ jsx(IconInfo, { tone: "positive" }),
              onClick: () => {
                setIconInfoPopup(true);
                analytics.onSalaryBadgeClicked();
              }
            }
          )
        ] })
      ] }) })
    ] }) : /* @__PURE__ */ jsx(Columns, { space: "small", children: /* @__PURE__ */ jsxs(Inline, { space: "none", alignY: "center", children: [
      /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(Text, { size: "small", children: /* @__PURE__ */ jsx(IconMoney, { tone: "secondary" }) }) }),
      /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(Stack, { space: "small", children: /* @__PURE__ */ jsxs(Inline, { space: "xxsmall", alignY: "center", children: [
        /* @__PURE__ */ jsx(
          Text,
          {
            size: "small",
            tone: "positive",
            data: { automation: "job-detail-salary-match" },
            children: t("Profile salary match")
          }
        ),
        /* @__PURE__ */ jsx(
          ButtonIcon,
          {
            size: "small",
            variant: "transparent",
            label: t("Salary match details"),
            id: "salaryMatchDetails",
            icon: /* @__PURE__ */ jsx(IconInfo, { tone: "positive" }),
            onClick: () => {
              setIconInfoPopup(true);
              analytics.onSalaryBadgeClicked();
            }
          }
        )
      ] }) }) })
    ] }) }),
    /* @__PURE__ */ jsx(
      SalaryMatchDetailsPopup,
      {
        salary,
        salaryExpectation,
        salaryMatchCountryName,
        iconInfoPopup,
        setIconInfoPopup
      }
    )
  ] });
};
const SalaryMatchDetailsPopup = ({
  salary,
  salaryExpectation,
  salaryMatchCountryName,
  iconInfoPopup,
  setIconInfoPopup
}) => {
  const { t } = useTranslations(translations);
  const salaryMatchDetails = salary ? t(
    "This job's salary range matches the expected salary you've set in your profile."
  ) : t(
    "The employer has chosen not to show the salary. This job's salary range matches the expected salary you've set in your profile."
  );
  return iconInfoPopup ? /* @__PURE__ */ jsxs(
    Dialog,
    {
      open: iconInfoPopup,
      onClose: () => setIconInfoPopup(false),
      id: "salaryMatchDetails",
      title: t("Salary match details"),
      children: [
        /* @__PURE__ */ jsxs(Text, { children: [
          " ",
          salaryMatchDetails,
          " "
        ] }),
        /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
          /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
            /* @__PURE__ */ jsx(Text, { weight: "medium", children: t("Salary expectation") }),
            salary ? /* @__PURE__ */ jsx(Text, { children: t("Edit in profile") }) : null
          ] }) }),
          /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
            /* @__PURE__ */ jsx(Text, { tone: "secondary", size: "small", children: salaryMatchCountryName }),
            /* @__PURE__ */ jsx(Text, { tone: "secondary", children: salaryExpectation })
          ] }) })
        ] })
      ]
    }
  ) : null;
};
const FormattedSalary = ({ salary, currencyLabel }) => {
  const formattedSalary = currencyLabel ? `${salary} (${currencyLabel})` : salary;
  return /* @__PURE__ */ jsxs(Columns, { space: "small", children: [
    /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(Text, { size: "small", children: /* @__PURE__ */ jsx(IconMoney, { tone: "secondary" }) }) }),
    /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(Stack, { space: "small", children: /* @__PURE__ */ jsx(Text, { size: "small", data: { automation: "job-detail-salary" }, children: formattedSalary }) }) })
  ] });
};
const AddExpectedSalary = () => {
  const { t } = useTranslations(translations);
  return /* @__PURE__ */ jsxs(Columns, { space: "small", children: [
    /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(Text, { size: "small", children: /* @__PURE__ */ jsx(IconMoney, { tone: "secondary" }) }) }),
    /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(Stack, { space: "small", children: /* @__PURE__ */ jsx(
      Text,
      {
        size: "small",
        tone: "secondary",
        data: { automation: "job-detail-add-expected-salary" },
        children: t("Add expected salary to your profile for insights")
      }
    ) }) })
  ] });
};
const PostedDate = () => {
  const { job } = useJobDetailsViewContext();
  const { t } = useTranslations(translations);
  return /* @__PURE__ */ jsx(Text, { size: "small", tone: "secondary", children: t("Posted {jobPostedDate}", {
    jobPostedDate: job.postedDateFormatted || ""
  }) });
};
const PostedDateAndNumOfApplicants = () => {
  const {
    featureFlags: { enableNumberOfApplicants } = {},
    applicationVolumeLabel
  } = useJobDetailsViewContext();
  return enableNumberOfApplicants && applicationVolumeLabel ? /* @__PURE__ */ jsxs(Inline, { space: "xxsmall", children: [
    /* @__PURE__ */ jsx(PostedDate, {}),
    /* @__PURE__ */ jsx(Text, { size: "small", tone: "secondary", children: "•" }),
    /* @__PURE__ */ jsx(Text, { size: "small", tone: "secondary", children: applicationVolumeLabel })
  ] }) : /* @__PURE__ */ jsx(PostedDate, {});
};
const JobDetailsViewHeaderAdditional = () => {
  const { job, seoInfo } = useJobDetailsViewContext();
  return /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
    /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
      job.location ? /* @__PURE__ */ jsx(
        JobLocation,
        {
          location: job.location,
          locationUrl: seoInfo?.locationUrl,
          workArrangementLabel: job.workArrangementLabel
        }
      ) : null,
      job.classifications ? /* @__PURE__ */ jsx(
        JobClassifications,
        {
          classifications: job.classifications,
          classificationUrls: seoInfo?.classificationUrls
        }
      ) : null,
      job.workType ? /* @__PURE__ */ jsx(
        JobWorkType,
        {
          workType: job.workType,
          workTypeUrl: seoInfo?.workTypeUrl
        }
      ) : null,
      job.isSignedIn && !job.salary && job.isCandidateSetSalary ? null : /* @__PURE__ */ jsx(
        JobSalary,
        {
          salary: job.salary,
          currencyLabel: job.currencyLabel,
          isSalaryMatch: job.isSalaryMatch,
          isSignedIn: job.isSignedIn,
          salaryExpectation: job.salaryExpectation,
          isCandidateSetSalary: job.isCandidateSetSalary,
          salaryMatchCountryName: job.salaryMatchCountryName
        }
      )
    ] }),
    /* @__PURE__ */ jsx(PostedDateAndNumOfApplicants, {})
  ] });
};
export {
  JobDetailsViewHeaderAdditional
};
